import { SyntheticEvent, useMemo, useState } from "react";
import { Box, Card, CardContent, Divider, Grid, Tab, Tabs, Typography } from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import dayjs from "dayjs";
import { userSelector } from "reduxStore/auth/selectors";
import { useAppSelector } from "hooks/useAppSelector";
import { ROLES } from "types/user";
import { organizationSelector, organizationSelectedSelector } from "reduxStore/organization/selectors";
import {
  useGetFeedbacksByOrganizationIdsQuery,
  useGetFeedbackViewsByOrgIdsQuery,
  useGetOrganizationByUserIdQuery,
  useGetOrganizationsQuery,
} from "graphql/__generated__/hooks";
import { DirectorOrgMetricsCharts, HospitalFeedbackCompetitionWidget } from "./components";
import {
  DEFAULT_METRICS_DATE_RANGE,
  getMetricsDateRangeLabel,
  getMetricsSinceDate,
  MetricsDateRange,
} from "./metricsDateRange";
import { getNonTestOrganizationIds, excludeTestOrganizationVariables } from "utils/testOrganization";

const TAB_KEYS = ["learners", "coaches", "managers"] as const;
type TabKey = (typeof TAB_KEYS)[number];

type MetricCard = {
  title: string;
  value: string;
  secondaryValue?: string;
  helper: string;
};

const percentage = (num: number, den: number) =>
  den > 0 ? `${Math.round((num / den) * 100)}%` : "-";

export const Metrics = () => {
  const user = useAppSelector(userSelector);
  const organizationsFromStore = useAppSelector(organizationSelector);
  const organizationSelected = useAppSelector(organizationSelectedSelector);
  const [selectedTab, setSelectedTab] = useState<TabKey>("coaches");
  const [dateRange, setDateRange] = useState<MetricsDateRange>(
    DEFAULT_METRICS_DATE_RANGE,
  );

  const isDirectorOrManager =
    user?.role === ROLES.DIRECTOR || user?.role === ROLES.MANAGER;
  const isAdmin = user?.role === ROLES.ADMIN;

  const { data: userOrganizationsData } = useGetOrganizationByUserIdQuery({
    variables: { userId: user?.user_id || "" },
    skip: !user?.user_id || isAdmin,
  });

  const { data: allOrganizationsData } = useGetOrganizationsQuery({
    skip: !isAdmin,
  });

  const accessibleOrgIds = useMemo(() => {
    if (isAdmin) {
      const organizations =
        allOrganizationsData?.organization ||
        organizationsFromStore ||
        [];
      return getNonTestOrganizationIds(organizations);
    }

    return getNonTestOrganizationIds(userOrganizationsData?.organization || []);
  }, [
    allOrganizationsData,
    isAdmin,
    organizationsFromStore,
    userOrganizationsData,
  ]);

  const sinceDate = useMemo(
    () => getMetricsSinceDate(dateRange),
    [dateRange],
  );
  const dateRangeLabel = getMetricsDateRangeLabel(dateRange);

  const { data: feedbackData, loading: feedbackLoading } =
    useGetFeedbacksByOrganizationIdsQuery({
      variables: {
        organizationIds: accessibleOrgIds,
        sinceDate,
        ...excludeTestOrganizationVariables,
      },
      skip: accessibleOrgIds.length === 0,
    });

  const { data: viewData, loading: viewLoading } = useGetFeedbackViewsByOrgIdsQuery({
    variables: {
      organizationIds: accessibleOrgIds,
      sinceDate,
      ...excludeTestOrganizationVariables,
    },
    skip: accessibleOrgIds.length === 0,
  });

  const feedbacks = feedbackData?.feedback || [];
  const feedbackViews = viewData?.feedback_view || [];

  const threeDaysAgo = dayjs().subtract(3, "day");
  const learnerFeedbacks = feedbacks.filter((feedback) => !!feedback.student_id);
  const coachFeedbacks = feedbacks.filter((feedback) => !!feedback.coach_id);
  const doneLearnerFeedbacks = learnerFeedbacks.filter(
    (feedback) => feedback.status === "DONE",
  );
  const doneCoachFeedbacks = coachFeedbacks.filter((feedback) => feedback.status === "DONE");
  const learnerSubmissionsInRange = learnerFeedbacks.filter((feedback) =>
    dayjs(feedback.created_at).isAfter(sinceDate),
  ).length;

  const overdueCoachTasks = coachFeedbacks.filter((feedback) => {
    if (feedback.status === "DONE") return false;
    return dayjs(feedback.created_at).isBefore(threeDaysAgo);
  }).length;

  const learnerRatingScores = learnerFeedbacks.flatMap((feedback) =>
    feedback.feedback_skills
      .map((skill) => skill.coach_rating)
      .filter((rating): rating is number => rating !== null && rating !== undefined),
  );

  const behindLearnerCount = learnerFeedbacks.filter((feedback) =>
    feedback.feedback_skills.some(
      (skill) => typeof skill.coach_rating === "number" && skill.coach_rating < 3,
    ),
  ).length;

  const learnerViews = feedbackViews.filter((view) => view.user?.role === "STUDENT");
  const coachViews = feedbackViews.filter((view) => view.user?.role === "COACH");
  const learnerViewsWithoutCompletion = learnerViews
    .filter((view) => view.feedback?.status !== "DONE")
    .reduce((sum, view) => sum + (view.count || 0), 0);
  const coachViewsWithoutCompletion = coachViews
    .filter((view) => view.feedback?.status !== "DONE")
    .reduce((sum, view) => sum + (view.count || 0), 0);

  const learnersByCoach = new Set(
    coachFeedbacks
      .map((feedback) => feedback.student_id)
      .filter((id): id is string => Boolean(id)),
  ).size;

  const learnersBehindByCoach = new Set(
    coachFeedbacks
      .filter((feedback) =>
        feedback.feedback_skills.some(
          (skill) =>
            typeof skill.coach_rating === "number" && skill.coach_rating < 3,
        ),
      )
      .map((feedback) => feedback.student_id)
      .filter((id): id is string => Boolean(id)),
  ).size;

  const totalCoachTasks = coachFeedbacks.length;
  const incompleteCoachTasks = coachFeedbacks.filter(
    (feedback) => feedback.status !== "DONE",
  ).length;

  const managerCards: MetricCard[] = [
    {
      title: "Completion",
      value: `Learners ${percentage(doneLearnerFeedbacks.length, learnerFeedbacks.length)}`,
      secondaryValue: `Coaches ${percentage(doneCoachFeedbacks.length, coachFeedbacks.length)}`,
      helper: "Completed task share by role in the selected period.",
    },
    {
      title: "Engagement",
      value: `Learners ${Math.round(learnerViewsWithoutCompletion / 2)}/wk`,
      secondaryValue: `Coaches ${Math.round(coachViewsWithoutCompletion / 2)}/wk`,
      helper: "Views per week without completing feedback tasks in the selected period.",
    },
    {
      title: "Progress",
      value: `Learners behind ${percentage(behindLearnerCount, learnerFeedbacks.length)}`,
      secondaryValue: `Coaches overdue ${percentage(overdueCoachTasks, totalCoachTasks)}`,
      helper: "Lagging learners and overdue coach task share in the selected period.",
    },
  ];

  const cardsByTab: Record<TabKey, MetricCard[]> = {
    learners: [
      {
        title: "Completion",
        value: percentage(doneLearnerFeedbacks.length, learnerFeedbacks.length),
        helper: "CBVE/EPA approvals completed in the selected period.",
      },
      {
        title: "Engagement",
        value: `${learnerSubmissionsInRange}`,
        helper: `Feedback submissions in the ${dateRangeLabel.toLowerCase()}.`,
      },
      {
        title: "Progress",
        value:
          learnerRatingScores.length > 0
            ? `${(learnerRatingScores.reduce((sum, value) => sum + value, 0) /
                learnerRatingScores.length).toFixed(1)} / 5`
            : "-",
        helper: "Average coach rating across learner feedback entries in the selected period.",
      },
    ],
    coaches: [
      {
        title: "Completion",
        value: `${percentage(incompleteCoachTasks, totalCoachTasks)} not complete`,
        helper: `${overdueCoachTasks} tasks overdue by more than 3 days in the selected period.`,
      },
      {
        title: "Engagement",
        value: `${Math.round(coachViewsWithoutCompletion / 2)}/wk`,
        helper: "Average views per week without completed feedback in the selected period.",
      },
      {
        title: "Progress",
        value: percentage(learnersBehindByCoach, learnersByCoach),
        helper: "Engaged learners currently behind in the selected period.",
      },
    ],
    managers: managerCards,
  };

  const loading = feedbackLoading || viewLoading;
  const tabByRole = useMemo<TabKey | null>(() => {
    if (user?.role === ROLES.STUDENT) return null;
    if (user?.role === ROLES.COACH) return "coaches";
    if (user?.role === ROLES.MANAGER || user?.role === ROLES.DIRECTOR)
      return "managers";
    return "coaches";
  }, [user]);
  const showTabs = user?.role === ROLES.ADMIN;
  const activeTab = showTabs ? selectedTab : tabByRole;
  const selectedCards = activeTab ? cardsByTab[activeTab] : [];

  const showDirectorCharts = isAdmin || (activeTab === "managers" && isDirectorOrManager);
  const competitionOrgId =
    (organizationSelected?.org_id &&
    accessibleOrgIds.includes(organizationSelected.org_id)
      ? organizationSelected.org_id
      : accessibleOrgIds[0]) || null;

  const handleTabChange = (_event: SyntheticEvent, value: TabKey) => {
    setSelectedTab(value);
  };

  return (
    <MainLayoutContainer title="Metrics">
      <Box>
        {showTabs && (
          <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab value="coaches" label="Coaches" />
            <Tab value="managers" label="Managers / Directors" />
          </Tabs>
        )}
        <Divider sx={{ my: 2 }} />
        {!activeTab && (
          <Typography variant="body1" color="text.secondary">
            Learner metrics are temporarily hidden.
          </Typography>
        )}
        <Grid container spacing={2}>
          {selectedCards.map((card) => (
            <Grid item xs={12} md={4} key={card.title}>
              <Card variant="outlined">
                <CardContent sx={{ minHeight: 180 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {card.title}
                  </Typography>
                  <Typography variant="h5">{loading ? "-" : card.value}</Typography>
                  {card.secondaryValue && (
                    <Typography variant="h5" sx={{ mt: 0.5 }}>
                      {loading ? "-" : card.secondaryValue}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {card.helper}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {showDirectorCharts && (
          <>
            <Box sx={{ mt: 3 }}>
              <HospitalFeedbackCompetitionWidget
                orgId={competitionOrgId}
                dateRange={dateRange}
              />
            </Box>
            <DirectorOrgMetricsCharts
              orgIds={accessibleOrgIds}
              sinceDate={sinceDate}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
          </>
        )}
      </Box>
    </MainLayoutContainer>
  );
};
